/**
 * Клиент REST-аутентификации (/api/auth/*). Используется стором.
 */
import type { CurrentUser } from './permissions';
import { authHeaders, setToken } from './session';

const BASE = '/api/auth';

async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((json as any)?.error || `HTTP ${res.status}`);
  return json as T;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { headers: { Accept: 'application/json', ...authHeaders() } });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((json as any)?.error || `HTTP ${res.status}`);
  return json as T;
}

interface AuthResult { user: CurrentUser; token: string }

/** Текущий пользователь по сохранённому токену (или null, если 401). */
export async function fetchMe(): Promise<CurrentUser | null> {
  try {
    const { user } = await get<{ user: CurrentUser }>('/me');
    return user;
  } catch {
    return null;
  }
}

export async function loginLocal(login: string, password: string): Promise<CurrentUser> {
  const r = await post<AuthResult>('/login', { login, password });
  setToken(r.token);
  return r.user;
}

export async function loginBitrix(domain: string, access_token: string): Promise<CurrentUser> {
  const r = await post<AuthResult>('/bitrix', { domain, access_token });
  setToken(r.token);
  return r.user;
}

/**
 * Какие способы входа доступны. Нужно экрану входа по прямой ссылке: кнопку
 * «Войти через Битрикс24» показываем, только если администратор прописал ключи
 * приложения в настройках (иначе портал нас не узнает).
 *
 * Вместе с флагом сервер отдаёт готовый адрес страницы подтверждения портала —
 * переходить по нему должен браузер, а не сервер (шлюз VibeCode «съедает» 3xx).
 */
export async function fetchAuthMethods(): Promise<{ bitrix_oauth: boolean; authorize_url?: string }> {
  try {
    return await get<{ bitrix_oauth: boolean; authorize_url?: string }>('/methods');
  } catch {
    return { bitrix_oauth: false };
  }
}

export async function logout(): Promise<void> {
  try { await post('/logout'); } catch { /* всё равно чистим локально */ }
  setToken(null);
}

/** Сменить свой пароль из профиля (для вошедших по логину). Бросает с текстом ошибки. */
export async function changePassword(current_password: string, new_password: string): Promise<void> {
  await post('/change-password', { current_password, new_password });
}

/** Частичное сохранение профиля (почта/телефон/фото/дата рождения). Возвращает обновлённого пользователя. */
export async function updateProfile(patch: { email?: string | null; phone?: string | null; avatar?: string | null; login?: string | null; birth_date?: string | null }): Promise<CurrentUser> {
  const { user } = await post<{ user: CurrentUser }>('/update-profile', patch);
  return user;
}

// ── Telegram-бот: привязка аккаунта ─────────────────────────
// Эти ручки живут вне /api/auth (под /api/bot/*), поэтому зовём их напрямую.

/** Запросить одноразовую ссылку привязки Telegram. Возвращает deep-link на бота. */
export async function requestTelegramLink(): Promise<{ deep_link: string; bot_username: string; expires_at: number }> {
  const res = await fetch('/api/bot/telegram/link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: '{}', // Fastify отклоняет пустой JSON-body (FST_ERR_CTP_EMPTY_JSON_BODY)
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((json as any)?.error || `HTTP ${res.status}`);
  return json as { deep_link: string; bot_username: string; expires_at: number };
}

/** Отвязать Telegram у текущего пользователя. */
export async function unlinkTelegram(): Promise<void> {
  const res = await fetch('/api/bot/telegram/unlink', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: '{}', // Fastify отклоняет пустой JSON-body (FST_ERR_CTP_EMPTY_JSON_BODY)
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((json as any)?.error || `HTTP ${res.status}`);
}

export interface InvitePublic {
  token: string;
  email: string | null;
  full_name: string | null;
  access_role: string | null;
  worker_type: string | null;
  expires_at: number;
  accepted_at: number | null;
  multi_use?: boolean;
}

export interface InviteBranch { id: string; name: string }
export interface InvitePosition { id: string; name: string; icon?: string | null }

export async function fetchInvite(token: string): Promise<{ invite: InvitePublic; branches: InviteBranch[]; positions: InvitePosition[] }> {
  const r = await get<{ invite: InvitePublic; branches?: InviteBranch[]; positions?: InvitePosition[] }>(`/invite/${encodeURIComponent(token)}`);
  return { invite: r.invite, branches: r.branches ?? [], positions: r.positions ?? [] };
}

export async function register(
  token: string, login: string, password: string,
  full_name?: string, phone?: string, branch_id?: string, worker_type?: string,
): Promise<{ pending: true } | { pending: false; user: CurrentUser }> {
  const r = await post<{ user?: CurrentUser; token?: string; pending?: boolean }>(
    '/register', { token, login, password, full_name, phone, branch_id, worker_type },
  );
  // По ОБЩЕЙ ссылке сервер отвечает { pending:true } без токена — вход не выдаётся,
  // человек ждёт подтверждения. По персональному инвайту — как раньше, авто-логин.
  if (r.pending || !r.token || !r.user) return { pending: true };
  setToken(r.token);
  return { pending: false, user: r.user };
}

export async function requestPasswordReset(login: string): Promise<void> {
  await post('/password-reset/request', { login });
}

export async function confirmPasswordReset(token: string, password: string): Promise<void> {
  await post('/password-reset/confirm', { token, password });
}
